// ======================================================================
// FILE:
// src/modules/finance/transactions/manual_journal/components/JournalHeaderForm.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Manual Journal - Create Journal
//
// PURPOSE:
// Reusable Journal Header form component used by the Manual Journal
// Create screen.
//
// RESPONSIBILITIES:
// - Display Journal Number
// - Capture Journal Date
// - Capture Journal Type
// - Search and select Company
// - Search and select Financial Year
// - Search and select Financial Period
// - Capture Currency
// - Capture Exchange Rate
// - Capture Journal Narration
// - Enforce Financial Year -> Financial Period dependency
//
// DOES NOT HANDLE:
// - Journal creation
// - Save Draft
// - Submit for Approval
// - Journal line management
// - Debit/Credit calculations
// - Navigation
// - Toast / notification handling
// - Global state management
//
// ARCHITECTURE:
// - React functional component
// - Bootstrap
// - React Select / AsyncSelect
// - Existing Finance lookup service
// - Controlled form
// - No Redux
//
// IMPORTANT:
// - Reference No. and Reference Date are intentionally excluded.
// - Financial Period is dependent on the selected Financial Year.
// - Currency and Journal Type options are supplied by the parent because
//   no dedicated lookup API for these was established.
// - Existing Finance architecture is preserved.
//
// AUTHOR:
// DizyPro ERP
// ======================================================================

import React from "react";
import AsyncSelect from "react-select/async";

// ======================================================================
// FINANCE LOOKUP SERVICE
// ======================================================================

import financeLookupService
    from "../services/financeLookupService";

// ======================================================================
// COMPONENT
// ======================================================================

export default function JournalHeaderForm({

    // --------------------------------------------------------------
    // Controlled form data
    // --------------------------------------------------------------

    formData = {},

    // --------------------------------------------------------------
    // Parent field change handler
    //
    // Expected signature:
    // onFieldChange(fieldName, value, selectedOption)
    // --------------------------------------------------------------

    onFieldChange,

    // --------------------------------------------------------------
    // Read-only / loading state
    // --------------------------------------------------------------

    disabled = false,

    loading = false,

    // --------------------------------------------------------------
    // Journal Number
    //
    // Generated by backend. Create screen displays it as read-only.
    // --------------------------------------------------------------

    journalNo = "",

    // --------------------------------------------------------------
    // Journal Type options
    //
    // Example:
    // [
    //     { value: "GENERAL", label: "General" }
    // ]
    //
    // Values must come from the existing Finance business definition.
    // This component does not invent Journal Types.
    // --------------------------------------------------------------

    journalTypeOptions = [],

    // --------------------------------------------------------------
    // Currency options
    //
    // Example:
    // [
    //     { value: 1, label: "INR - Indian Rupee" }
    // ]
    //
    // Currency lookup source will be supplied by parent after it is
    // verified against the existing Finance architecture.
    // --------------------------------------------------------------

    currencyOptions = [],

    // --------------------------------------------------------------
    // Selected lookup options
    //
    // These are kept separately from IDs because react-select needs
    // the complete option object to display the selected value.
    // --------------------------------------------------------------

    selectedCompany = null,

    selectedFinancialYear = null,

    selectedFinancialPeriod = null,

    selectedCurrency = null,

}) {

    // ==================================================================
    // SAFE FIELD CHANGE HANDLER
    // ==================================================================

    const handleFieldChange = (
        fieldName,
        value,
        selectedOption = null
    ) => {

        if (typeof onFieldChange !== "function") {
            return;
        }

        onFieldChange(
            fieldName,
            value,
            selectedOption
        );
    };

    // ==================================================================
    // COMPANY LOOKUP
    // ==================================================================

    const loadCompanies = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService.searchCompanies(
                    inputValue
                );

            return (
                response || []
            ).map(
                (company) => ({

                    value:
                        company.id,

                    label:
                        company.company_code
                            ? `${company.company_code} - ${company.company_name}`
                            : company.company_name,

                    // --------------------------------------------------
                    // Preserve original API object.
                    // Useful to the parent if additional company data
                    // is required later.
                    // --------------------------------------------------

                    data:
                        company,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load finance companies:",
                error
            );

            return [];
        }
    };

    // ==================================================================
    // FINANCIAL YEAR LOOKUP
    // ==================================================================

    const loadFinancialYears = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService.searchFinancialYears(
                    inputValue
                );

            return (
                response || []
            ).map(
                (financialYear) => ({

                    value:
                        financialYear.id,

                    label:
                        financialYear.year_name ||
                        financialYear.name ||
                        `Financial Year ${financialYear.id}`,

                    // --------------------------------------------------
                    // Preserve original API object.
                    // --------------------------------------------------

                    data:
                        financialYear,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load financial years:",
                error
            );

            return [];
        }
    };

    // ==================================================================
    // FINANCIAL PERIOD LOOKUP
    // ==================================================================

    const loadFinancialPeriods = async (
        inputValue = ""
    ) => {

        // --------------------------------------------------------------
        // Financial Period cannot be searched without a Financial Year.
        // This dependency is already established in the Journal List
        // implementation and backend lookup API.
        // --------------------------------------------------------------

        if (!formData?.financial_year_id) {
            return [];
        }

        try {

            const response =
                await financeLookupService.searchFinancialPeriods(
                    formData.financial_year_id,
                    inputValue
                );

            return (
                response || []
            ).map(
                (financialPeriod) => ({

                    value:
                        financialPeriod.id,

                    label:
                        financialPeriod.period_name ||
                        financialPeriod.period_code ||
                        `Period ${financialPeriod.id}`,

                    // --------------------------------------------------
                    // Preserve original API object.
                    // --------------------------------------------------

                    data:
                        financialPeriod,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load financial periods:",
                error
            );

            return [];
        }
    };

    // ==================================================================
    // COMPANY CHANGE
    // ==================================================================

    const handleCompanyChange = (
        option
    ) => {

        handleFieldChange(
            "company_id",
            option?.value || null,
            option || null
        );
    };

    // ==================================================================
    // FINANCIAL YEAR CHANGE
    // ==================================================================

    const handleFinancialYearChange = (
        option
    ) => {

        handleFieldChange(
            "financial_year_id",
            option?.value || null,
            option || null
        );

        // --------------------------------------------------------------
        // A Financial Period belongs to a Financial Year.
        //
        // Therefore the previously selected period must be cleared when
        // the Financial Year changes.
        // --------------------------------------------------------------

        handleFieldChange(
            "financial_period_id",
            null,
            null
        );
    };

    // ==================================================================
    // FINANCIAL PERIOD CHANGE
    // ==================================================================

    const handleFinancialPeriodChange = (
        option
    ) => {

        handleFieldChange(
            "financial_period_id",
            option?.value || null,
            option || null
        );
    };

    // ==================================================================
    // CURRENCY CHANGE
    // ==================================================================

    const handleCurrencyChange = (
        event
    ) => {

        const value =
            event.target.value || null;

        const selectedOption =
            currencyOptions.find(
                (option) =>
                    String(option.value) ===
                    String(value)
            ) || null;

        handleFieldChange(
            "currency_id",
            value,
            selectedOption
        );
    };

    // ==================================================================
    // JOURNAL TYPE CHANGE
    // ==================================================================

    const handleJournalTypeChange = (
        event
    ) => {

        const value =
            event.target.value || "";

        const selectedOption =
            journalTypeOptions.find(
                (option) =>
                    String(option.value) ===
                    String(value)
            ) || null;

        handleFieldChange(
            "journal_type",
            value,
            selectedOption
        );
    };

    // ==================================================================
    // JOURNAL DATE CHANGE
    // ==================================================================

    const handleJournalDateChange = (
        event
    ) => {

        handleFieldChange(
            "journal_date",
            event.target.value || ""
        );
    };

    // ==================================================================
    // EXCHANGE RATE CHANGE
    // ==================================================================

    const handleExchangeRateChange = (
        event
    ) => {

        handleFieldChange(
            "exchange_rate",
            event.target.value
        );
    };

    // ==================================================================
    // NARRATION CHANGE
    // ==================================================================

    const handleNarrationChange = (
        event
    ) => {

        // --------------------------------------------------------------
        // Existing Finance model/API uses "remarks".
        // We therefore do not introduce a new "narration" field.
        // --------------------------------------------------------------

        handleFieldChange(
            "remarks",
            event.target.value
        );
    };

    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <div className="card shadow-sm border-0 mb-4">

            {/* ==========================================================
                SECTION HEADER
            ========================================================== */}

            <div className="card-header bg-white border-bottom">

                <div className="d-flex align-items-center">

                    <div
                        className="
                            rounded-circle
                            bg-primary
                            text-white
                            d-flex
                            align-items-center
                            justify-content-center
                            me-2
                        "
                        style={{
                            width: 30,
                            height: 30,
                            fontSize: 14,
                            fontWeight: 600,
                        }}
                    >
                        1
                    </div>

                    <span className="fw-semibold">
                        Journal Header
                    </span>

                </div>

            </div>

            {/* ==========================================================
                SECTION BODY
            ========================================================== */}

            <div className="card-body">

                <div className="row g-3">

                    {/* ==================================================
                        JOURNAL NUMBER
                    ================================================== */}

                    <div className="col-xl-4 col-lg-4 col-md-6 col-12">

                        <label className="form-label">
                            Journal No. *
                        </label>

                        <input
                            type="text"
                            className="form-control"
                            value={
                                journalNo || "Auto"
                            }
                            readOnly
                            disabled
                        />

                    </div>

                    {/* ==================================================
                        JOURNAL DATE
                    ================================================== */}

                    <div className="col-xl-4 col-lg-4 col-md-6 col-12">

                        <label className="form-label">
                            Journal Date *
                        </label>

                        <input
                            type="date"
                            className="form-control"
                            value={
                                formData?.journal_date || ""
                            }
                            onChange={
                                handleJournalDateChange
                            }
                            disabled={
                                disabled ||
                                loading
                            }
                        />

                    </div>

                    {/* ==================================================
                        JOURNAL TYPE
                    ================================================== */}

                    <div className="col-xl-4 col-lg-4 col-md-6 col-12">

                        <label className="form-label">
                            Journal Type *
                        </label>

                        <select
                            className="form-select"
                            value={
                                formData?.journal_type || ""
                            }
                            onChange={
                                handleJournalTypeChange
                            }
                            disabled={
                                disabled ||
                                loading
                            }
                        >

                            <option value="">
                                Select Journal Type
                            </option>

                            {journalTypeOptions.map(
                                (option) => (

                                    <option
                                        key={
                                            option.value
                                        }
                                        value={
                                            option.value
                                        }
                                    >
                                        {option.label}
                                    </option>

                                )
                            )}

                        </select>

                    </div>

                    {/* ==================================================
                        COMPANY
                    ================================================== */}

                    <div className="col-xl-4 col-lg-4 col-md-6 col-12">

                        <label className="form-label">
                            Company *
                        </label>

                        <AsyncSelect
                            cacheOptions
                            defaultOptions
                            isClearable
                            isSearchable
                            value={
                                selectedCompany
                            }
                            loadOptions={
                                loadCompanies
                            }
                            placeholder={
                                "Select Company"
                            }
                            onChange={
                                handleCompanyChange
                            }
                            isDisabled={
                                disabled ||
                                loading
                            }
                            noOptionsMessage={() =>
                                "No companies found"
                            }
                            loadingMessage={() =>
                                "Loading companies..."
                            }
                        />

                    </div>

                    {/* ==================================================
                        FINANCIAL YEAR
                    ================================================== */}

                    <div className="col-xl-4 col-lg-4 col-md-6 col-12">

                        <label className="form-label">
                            Financial Year *
                        </label>

                        <AsyncSelect
                            cacheOptions
                            defaultOptions
                            isClearable
                            isSearchable
                            value={
                                selectedFinancialYear
                            }
                            loadOptions={
                                loadFinancialYears
                            }
                            placeholder={
                                "Select Financial Year"
                            }
                            onChange={
                                handleFinancialYearChange
                            }
                            isDisabled={
                                disabled ||
                                loading
                            }
                            noOptionsMessage={() =>
                                "No financial years found"
                            }
                            loadingMessage={() =>
                                "Loading financial years..."
                            }
                        />

                    </div>

                    {/* ==================================================
                        FINANCIAL PERIOD
                    ================================================== */}

                    <div className="col-xl-4 col-lg-4 col-md-6 col-12">

                        <label className="form-label">
                            Financial Period *
                        </label>

                        <AsyncSelect
                            key={
                                formData?.financial_year_id ||
                                "no-financial-year"
                            }
                            cacheOptions
                            defaultOptions
                            isClearable
                            isSearchable
                            value={
                                selectedFinancialPeriod
                            }
                            loadOptions={
                                loadFinancialPeriods
                            }
                            placeholder={
                                formData?.financial_year_id
                                    ? "Select Financial Period"
                                    : "Select Financial Year First"
                            }
                            onChange={
                                handleFinancialPeriodChange
                            }
                            isDisabled={
                                disabled ||
                                loading ||
                                !formData?.financial_year_id
                            }
                            noOptionsMessage={() =>
                                "No financial periods found"
                            }
                            loadingMessage={() =>
                                "Loading financial periods..."
                            }
                        />

                    </div>

                    {/* ==================================================
                        CURRENCY
                    ================================================== */}

                    <div className="col-xl-4 col-lg-4 col-md-6 col-12">

                        <label className="form-label">
                            Currency *
                        </label>

                        <select
                            className="form-select"
                            value={
                                formData?.currency_id || ""
                            }
                            onChange={
                                handleCurrencyChange
                            }
                            disabled={
                                disabled ||
                                loading
                            }
                        >

                            <option value="">
                                Select Currency
                            </option>

                            {currencyOptions.map(
                                (option) => (

                                    <option
                                        key={
                                            option.value
                                        }
                                        value={
                                            option.value
                                        }
                                    >
                                        {option.label}
                                    </option>

                                )
                            )}

                        </select>

                    </div>

                    {/* ==================================================
                        EXCHANGE RATE
                    ================================================== */}

                    <div className="col-xl-4 col-lg-4 col-md-6 col-12">

                        <label className="form-label">
                            Exchange Rate *
                        </label>

                        <input
                            type="number"
                            className="form-control"
                            min="0"
                            step="0.00000001"
                            value={
                                formData?.exchange_rate ??
                                ""
                            }
                            onChange={
                                handleExchangeRateChange
                            }
                            disabled={
                                disabled ||
                                loading
                            }
                        />

                    </div>

                    {/* ==================================================
                        NARRATION
                    ================================================== */}

                    <div className="col-xl-8 col-lg-8 col-12">

                        <label className="form-label">
                            Narration *
                        </label>

                        <textarea
                            className="form-control"
                            rows="4"
                            maxLength="1000"
                            placeholder={
                                "Enter journal narration..."
                            }
                            value={
                                formData?.remarks || ""
                            }
                            onChange={
                                handleNarrationChange
                            }
                            disabled={
                                disabled ||
                                loading
                            }
                        />

                        <div className="text-end text-muted small mt-1">

                            {
                                (formData?.remarks || "")
                                    .length
                            }
                            /1000

                        </div>

                    </div>

                </div>

            </div>

        </div>
    );
}